/**
 * Modo Corrida GPS — lógica pura (sem DOM, sem rede, sem relógio próprio, sem gravação).
 * v0.16.0 (B1): matemática (haversine, filtros). v0.16.2 (B3): máquina de estados e corpo do envio.
 * Fica num arquivo só porque os testes `node --test` não resolvem imports sem extensão (D2: sem mudar a configuração).
 *
 * Parâmetros: ponto de partida validado no desenho (docs/24-modo-corrida-gps.md), a calibrar em aparelho real na B6.
 */

// ============================================================================ matemática e filtros (B1)

export interface GpsFix {
  lat: number;
  lon: number;
  /** Precisão horizontal informada pelo aparelho, em metros (raio de 68%). */
  accuracy: number;
  /** Instante da leitura, em ms desde a época (Position.timestamp). */
  ts: number;
  /** Altitude em metros, quando o aparelho informa. */
  alt?: number | null;
}

export const GPS_PARAMS = {
  /** Descarta leituras piores que isto (m). */
  maxAccuracyM: 30,
  /** Passo mínimo para contar distância (m): evita "andar parado" por ruído. */
  minStepM: 3,
  /** Velocidade máxima plausível a pé/corrida (m/s ≈ 43 km/h): acima disso é salto de sinal. */
  maxSpeedMps: 12,
  /** Intervalo sem leitura aceita a partir do qual não se liga os pontos (s): o trecho vira "lacuna". */
  maxGapS: 30,
} as const;

const R = 6371008.8; // raio médio da Terra (m)
const rad = (d: number) => (d * Math.PI) / 180;

/** Distância geodésica (haversine) entre duas coordenadas, em metros. */
export function haversineM(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const dLat = rad(b.lat - a.lat);
  const dLon = rad(b.lon - a.lon);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

export type Verdict = "first" | "accepted" | "poor_accuracy" | "invalid" | "out_of_order" | "too_close" | "too_fast";

export interface Tracker {
  /** Último ponto aceito. */
  last: GpsFix | null;
  distanceM: number;
  accepted: number;
  rejected: number;
}

export const newTracker = (): Tracker => ({ last: null, distanceM: 0, accepted: 0, rejected: 0 });

export function validFix(f: GpsFix): boolean {
  return Number.isFinite(f.lat) && Number.isFinite(f.lon) && Math.abs(f.lat) <= 90 && Math.abs(f.lon) <= 180
    && Number.isFinite(f.accuracy) && f.accuracy >= 0 && Number.isFinite(f.ts);
}

/** Decide sobre uma leitura e devolve o novo estado (não altera o anterior). `linked` = o ponto se liga ao anterior (soma distância). */
export function feed(prev: Tracker, f: GpsFix, p = GPS_PARAMS): { next: Tracker; verdict: Verdict; linked: boolean } {
  const reject = (verdict: Verdict) => ({ next: { ...prev, rejected: prev.rejected + 1 }, verdict, linked: false });
  if (!validFix(f)) return reject("invalid");
  if (f.accuracy > p.maxAccuracyM) return reject("poor_accuracy");
  if (!prev.last) return { next: { last: f, distanceM: prev.distanceM, accepted: prev.accepted + 1, rejected: prev.rejected }, verdict: "first", linked: false };
  const dt = (f.ts - prev.last.ts) / 1000;
  if (dt <= 0) return reject("out_of_order");
  const step = haversineM(prev.last, f);
  if (step < p.minStepM) return reject("too_close");
  if (step / dt > p.maxSpeedMps) return reject("too_fast");
  // Lacuna longa: o ponto é aceito como nova âncora, mas o trecho sem leitura não soma distância.
  const add = dt > p.maxGapS ? 0 : step;
  return { next: { last: f, distanceM: prev.distanceM + add, accepted: prev.accepted + 1, rejected: prev.rejected }, verdict: "accepted", linked: dt <= p.maxGapS };
}

/** Ritmo médio em segundos por km; null sem distância ou tempo. */
export function paceSecPerKm(distanceM: number, movingS: number): number | null {
  if (!(distanceM > 0) || !(movingS > 0)) return null;
  return movingS / (distanceM / 1000);
}

/** Reduz a rota a no máximo `max` pontos, mantendo primeiro e último (mesma ideia do ActivityStream, ~2000). */
export function downsample<T>(pts: T[], max = 2000): T[] {
  if (max < 2 || pts.length <= max) return pts.slice();
  const out: T[] = [];
  const step = (pts.length - 1) / (max - 1);
  for (let i = 0; i < max; i++) out.push(pts[Math.round(i * step)]);
  return out;
}


// ============================================================================ máquina de estados (B3)

/**
 * máquina de estados e cálculo do Modo Corrida GPS. Lógica pura: sem DOM, sem rede, sem relógio
 * próprio (o instante entra por parâmetro) e sem gravação. A captura real fica em `useGpsRecorder`.
 *
 * Estados: idle → checking → requesting_location → recording ⇄ paused → finalizing → saved; error (só antes de gravar).
 * `saved` aqui significa "confirmado na memória": guardar no aparelho e enviar é a B4 (pending_sync entra lá).
 *
 * Definições (mostradas na tela com estes nomes):
 * - tempo decorrido: do início ao fim, incluindo pausas;
 * - tempo ativo: só os trechos em `recording`;
 * - distância: soma haversine entre pontos aceitos e ligados (regras de gpsMath);
 * - ritmo médio: tempo ativo / distância, somente com distância ≥ MIN_PACE_DISTANCE_M.
 */

export type Status = "idle" | "checking" | "requesting_location" | "recording" | "paused" | "finalizing" | "saved" | "error";
export type Problem = "denied" | "unsupported" | "insecure" | "unavailable" | "timeout" | "storage" | "full" | "interrupted" | null;

export const MIN_PACE_DISTANCE_M = 100;
export const MIN_ROUTE_DISTANCE_M = 10; // o servidor rejeita menos que isso (B2)
export const MAX_POINTS = 2500;          // limite do servidor (B2)
const STALE_MS = 5000;                   // leitura com horário anterior ao início por mais que isso é de cache

export interface RoutePoint { t: number; lat: number; lon: number; alt: number | null; acc: number; gap: boolean }
export interface Split { km: number; activeS: number; paceSPerKm: number }

export interface Recorder {
  status: Status;
  clientId: string | null;
  startedAt: number | null;
  finishedAt: number | null;
  activeMs: number;          // tempo ativo já fechado (sem o trecho em andamento)
  segStart: number | null;   // início do trecho de gravação em andamento
  tracker: Tracker;
  points: RoutePoint[];
  splits: Split[];
  prev: { d: number; a: number } | null;   // último ponto ligado: distância e tempo ativo
  splitActiveMs: number;
  lastFixAt: number | null;  // `now` da última leitura aceita
  lastAccuracy: number | null;
  problem: Problem;
}

export const newRecorder = (): Recorder => ({
  status: "idle", clientId: null, startedAt: null, finishedAt: null, activeMs: 0, segStart: null, tracker: newTracker(),
  points: [], splits: [], prev: null, splitActiveMs: 0, lastFixAt: null, lastAccuracy: null, problem: null,
});

export type Ev =
  | { type: "check" }
  | { type: "precheck_ok" }
  | { type: "precheck_fail"; problem: Exclude<Problem, null> }
  | { type: "fix"; fix: GpsFix; now: number; id: string }
  | { type: "signal"; problem: "unavailable" | "timeout" }
  | { type: "denied"; now: number }
  | { type: "pause"; now: number }
  | { type: "resume"; now: number }
  | { type: "finish"; now: number }
  | { type: "confirm" }
  | { type: "discard" }
  | { type: "reset" }
  | { type: "load"; state: Recorder };

/** Transições permitidas. Qualquer outra é ignorada (devolve o mesmo objeto). */
const ALLOWED: Record<Ev["type"], Status[]> = {
  check: ["idle"],
  precheck_ok: ["checking"],
  precheck_fail: ["checking"],
  fix: ["requesting_location", "recording"],
  signal: ["requesting_location", "recording"],
  denied: ["requesting_location", "recording"],
  pause: ["recording"],
  resume: ["paused"],
  finish: ["recording", "paused"],
  confirm: ["finalizing"],
  discard: ["recording", "paused", "finalizing", "error", "requesting_location", "checking"],
  reset: ["saved", "error", "idle"],
  load: ["idle"],
};

export const canDo = (r: Recorder, type: Ev["type"]) => ALLOWED[type].includes(r.status);

const closeSegment = (r: Recorder, now: number): Recorder =>
  r.segStart === null ? r : { ...r, activeMs: r.activeMs + Math.max(0, now - r.segStart), segStart: null };

function addFix(r: Recorder, fix: GpsFix, now: number, first = false): Recorder {
  const startedAt = r.startedAt as number;
  const reject = (): Recorder => ({ ...r, tracker: { ...r.tracker, rejected: r.tracker.rejected + 1 } });
  if (!Number.isFinite(fix.ts) || (!first && fix.ts < startedAt - STALE_MS)) return reject();
  const t = Math.max(0, Math.round((fix.ts - startedAt) / 1000));
  const last = r.points[r.points.length - 1];
  if (last && t <= last.t) return reject();   // mesmo segundo ou fora de ordem: regra determinística, ponto descartado
  const res = feed(r.tracker, fix);
  if (res.verdict !== "first" && res.verdict !== "accepted") {
    return { ...r, tracker: res.next, lastAccuracy: Number.isFinite(fix.accuracy) ? fix.accuracy : r.lastAccuracy };
  }
  const activeNow = r.activeMs + (r.segStart !== null ? Math.max(0, now - r.segStart) : 0);
  const point: RoutePoint = { t, lat: fix.lat, lon: fix.lon, alt: fix.alt ?? null, acc: fix.accuracy, gap: !!last && !res.linked };
  const splits = r.splits.slice();
  let splitActiveMs = r.splitActiveMs;
  if (res.linked && r.prev) {
    const d0 = r.prev.d, d1 = res.next.distanceM;
    while (d1 >= (splits.length + 1) * 1000 && d1 > d0) {
      const target = (splits.length + 1) * 1000;
      const at = r.prev.a + ((target - d0) / (d1 - d0)) * (activeNow - r.prev.a);
      const secs = (at - splitActiveMs) / 1000;
      splits.push({ km: splits.length + 1, activeS: Math.round(secs), paceSPerKm: Math.round(secs) });
      splitActiveMs = at;
    }
  }
  return {
    ...r, tracker: res.next, points: [...r.points, point], splits, splitActiveMs,
    prev: { d: res.next.distanceM, a: activeNow }, lastFixAt: now, lastAccuracy: fix.accuracy, problem: null,
  };
}

export function reduce(r: Recorder, ev: Ev): Recorder {
  if (!canDo(r, ev.type)) return r;
  switch (ev.type) {
    case "check": return { ...newRecorder(), status: "checking" };
    case "precheck_ok": return { ...r, status: "requesting_location" };
    case "precheck_fail": return { ...r, status: "error", problem: ev.problem };
    case "fix": {
      if (r.status === "requesting_location") {
        // A gravação (e o relógio) começa na primeira leitura válida, não no toque em Iniciar: esperar o GPS não conta.
        if (!Number.isFinite(ev.fix.lat) || !Number.isFinite(ev.fix.lon)) return r;
        const started: Recorder = { ...r, status: "recording", startedAt: ev.now, segStart: ev.now, clientId: ev.id, problem: null };
        return addFix(started, ev.fix, ev.now, true);
      }
      return addFix(r, ev.fix, ev.now);
    }
    case "signal": return { ...r, problem: ev.problem };
    case "denied":
      // Sem permissão: antes de gravar vira erro; gravando, pausa e preserva tudo o que já foi captado.
      return r.status === "recording" ? { ...closeSegment(r, ev.now), status: "paused", problem: "denied" }
        : { ...r, status: "error", problem: "denied" };
    case "pause": return { ...closeSegment(r, ev.now), status: "paused" };
    case "resume": return { ...r, status: "recording", segStart: ev.now, problem: null, tracker: { ...r.tracker, last: null }, prev: null };
    case "finish": return { ...closeSegment(r, ev.now), status: "finalizing", finishedAt: ev.now };
    case "confirm": return { ...r, status: "saved" };
    case "discard": case "reset": return newRecorder();
    case "load": return ev.state;
  }
}

// ----------------------------------------------------------------------------- leitura para a tela

export function activeMs(r: Recorder, now: number): number {
  return r.activeMs + (r.segStart !== null ? Math.max(0, now - r.segStart) : 0);
}
export function elapsedMs(r: Recorder, now: number): number {
  if (r.startedAt === null) return 0;
  return Math.max(0, (r.finishedAt ?? now) - r.startedAt);
}
export function avgPaceSPerKm(distanceM: number, activeMsValue: number): number | null {
  if (!(distanceM >= MIN_PACE_DISTANCE_M) || !(activeMsValue > 0)) return null;
  return activeMsValue / 1000 / (distanceM / 1000);
}
/** Idade da última posição aceita (ms); null se ainda não houve. */
export const fixAgeMs = (r: Recorder, now: number): number | null => (r.lastFixAt === null ? null : Math.max(0, now - r.lastFixAt));
/** A gravação tem o mínimo para virar atividade (o servidor exige ≥ 2 pontos e ≥ 10 m). */
export const hasEnoughRoute = (r: Recorder): boolean => r.points.length >= 2 && r.tracker.distanceM >= MIN_ROUTE_DISTANCE_M;

// ----------------------------------------------------------------------------- rota e corpo do envio (formato da B2)

/**
 * Reduz a rota a no máximo `max` pontos. Pontos de lacuna e o anterior a cada lacuna são sempre mantidos: se um
 * deles caísse, o servidor ligaria os dois lados da pausa com uma linha reta e somaria distância que não existiu.
 */
export function reduceRoute(pts: RoutePoint[], max = MAX_POINTS): RoutePoint[] {
  if (pts.length <= max) return pts.slice();
  const must = new Set<number>([0, pts.length - 1]);
  pts.forEach((p, i) => { if (p.gap) { must.add(i); must.add(i - 1); } });
  const free = pts.map((_, i) => i).filter((i) => !must.has(i));
  const budget = max - must.size;
  let keep: Set<number>;
  if (budget <= 0) {
    // Caso extremo (milhares de lacunas): amostragem uniforme simples; o servidor aceita, só perde fidelidade.
    const step = (pts.length - 1) / (max - 1);
    keep = new Set(Array.from({ length: max }, (_, i) => Math.round(i * step)));
  } else {
    keep = new Set(must);
    const step = free.length / budget;
    for (let i = 0; i < budget; i++) keep.add(free[Math.min(free.length - 1, Math.floor(i * step))]);
  }
  return [...keep].sort((a, b) => a - b).map((i) => pts[i]);
}

export interface GpsRecordingBody {
  client_id: string;
  sport: "run" | "trail_run" | "walk";
  name?: string;
  started_at: string;
  moving_time_s: number;
  elapsed_time_s: number;
  points: { t: number; lat: number; lon: number; alt?: number; acc?: number; gap?: boolean }[];
}

/** Corpo de `POST /me/gps-recordings`. Só depois de finalizar; devolve null se a gravação é curta demais. */
export function buildBody(r: Recorder, sport: GpsRecordingBody["sport"] = "run"): GpsRecordingBody | null {
  if ((r.status !== "finalizing" && r.status !== "saved") || r.startedAt === null || r.finishedAt === null || !r.clientId) return null;
  if (!hasEnoughRoute(r)) return null;
  const pts = reduceRoute(r.points);
  const lastT = pts[pts.length - 1].t;
  const elapsed = Math.max(1, Math.ceil((r.finishedAt - r.startedAt) / 1000), lastT);
  const moving = Math.min(elapsed, Math.max(1, Math.round(r.activeMs / 1000)));
  return {
    client_id: r.clientId, sport, started_at: new Date(r.startedAt).toISOString(), moving_time_s: moving, elapsed_time_s: elapsed,
    points: pts.map((p) => ({
      t: p.t, lat: round(p.lat, 6), lon: round(p.lon, 6),
      ...(p.alt !== null && Number.isFinite(p.alt) ? { alt: round(p.alt, 1) } : {}),
      ...(Number.isFinite(p.acc) ? { acc: round(p.acc, 1) } : {}),
      ...(p.gap ? { gap: true } : {}),
    })),
  };
}
const round = (v: number, d: number) => Math.round(v * 10 ** d) / 10 ** d;

export const PARAMS_DOC = { ...GPS_PARAMS, minPaceDistanceM: MIN_PACE_DISTANCE_M, minRouteDistanceM: MIN_ROUTE_DISTANCE_M, maxPoints: MAX_POINTS };

// ----------------------------------------------------------------------------- pré-verificação

export interface GpsEnv {
  secure: boolean;
  hasGeo: boolean;
  permission: "granted" | "prompt" | "denied" | "unknown";
}

/** Motivo que impede gravar, ou null se pode tentar. Permissão "prompt"/"unknown" não bloqueia: o pedido vem no clique. */
export function precheck(env: GpsEnv): Exclude<Problem, null> | null {
  if (!env.secure) return "insecure";
  if (!env.hasGeo) return "unsupported";
  if (env.permission === "denied") return "denied";
  return null;
}

// ----------------------------------------------------------------------------- recuperação (B4)

/** Eventos que a B4 grava no aparelho: bastam para reconstruir a gravação reaplicando a máquina de estados. */
export const PERSISTED_EVENTS: Ev["type"][] = ["fix", "pause", "resume", "finish", "confirm", "denied"];
export const isPersisted = (ev: Ev): boolean => PERSISTED_EVENTS.includes(ev.type);

const evTime = (ev: Ev): number => ("now" in ev ? ev.now : 0);

/**
 * Reconstrói a gravação a partir dos eventos guardados. Se ela estava em `recording` (a página caiu), volta
 * **pausada no instante do último evento**: o tempo em que o aparelho ficou sem gravar não conta como ativo e o
 * usuário decide entre retomar, finalizar ou descartar. Devolve também o evento de pausa sintético, que deve ser
 * guardado para que uma nova reconstrução dê o mesmo resultado.
 */
export function recoverInterrupted(events: Ev[]): { state: Recorder; synthetic: Ev | null } | null {
  let r = reduce(reduce(newRecorder(), { type: "check" }), { type: "precheck_ok" });
  for (const ev of events) r = reduce(r, ev);
  if (r.status === "requesting_location" || r.status === "idle") return null;   // nunca chegou a gravar
  if (r.status === "recording") {
    const at = events.reduce((m, ev) => Math.max(m, evTime(ev)), 0);
    const synthetic: Ev = { type: "pause", now: at };
    return { state: reduce(r, synthetic), synthetic };
  }
  return { state: r, synthetic: null };
}

// ----------------------------------------------------------------------------- contexto do aparelho e qualidade do sinal (B5)

/**
 * v0.16.6: captura ligada dentro do app Capacitor, **para teste em aparelho real** (docs/25, T3–T13). Se a WebView não
 * entregar a posição, o app mostra "permissão negada"; para voltar ao navegador do celular basta trocar para `false`
 * (e gerar o APK de novo). Sem serviço em primeiro plano e sem GPS em segundo plano.
 */
export const GPS_IN_APP = true;

export interface DeviceEnv {
  coarsePrimary: boolean;      // matchMedia("(pointer: coarse)"): o dispositivo de entrada principal é o dedo
  coarseAny: boolean;          // matchMedia("(any-pointer: coarse)")
  maxTouchPoints: number;
  uaMobile: boolean | null;    // navigator.userAgentData?.mobile; null se o navegador não informa
  isApp: boolean;
}
export type CaptureContext = { kind: "mobile" | "desktop" | "app"; allowed: boolean; reason: "desktop" | "app" | null };

/**
 * Decide se este aparelho oferece o comando de gravar. Por capacidades, nunca só por largura de tela nem só por
 * user-agent: o dedo como entrada principal basta; toque secundário (notebook com tela sensível) só conta se o
 * navegador também declara que é um aparelho móvel.
 */
export function captureContext(env: DeviceEnv): CaptureContext {
  if (env.isApp) return GPS_IN_APP ? { kind: "app", allowed: true, reason: null } : { kind: "app", allowed: false, reason: "app" };
  const mobile = env.coarsePrimary || (env.coarseAny && env.maxTouchPoints > 0 && env.uaMobile === true);
  return mobile ? { kind: "mobile", allowed: true, reason: null } : { kind: "desktop", allowed: false, reason: "desktop" };
}

export type SignalQuality = "none" | "good" | "fair" | "weak" | "poor";

/** Qualidade da última posição para mostrar ao usuário (sem inventar precisão): `age`/`accuracy` em ms e metros. */
export function signalQuality(accuracy: number | null, ageMs: number | null): SignalQuality {
  if (accuracy === null || ageMs === null || ageMs > 15_000) return "none";
  if (accuracy <= 10) return "good";
  if (accuracy <= 20) return "fair";
  if (accuracy <= GPS_PARAMS.maxAccuracyM) return "weak";
  return "poor";
}
